import type { OnboardingStatus } from "@/hooks/use-onboarding";

/**
 * Roles never asked. `end_user` gets a 403 from every onboarding route and
 * lives in the embed; `platform_admin` resolution is a cross-repo contract
 * (engineering rule 19) this must not touch.
 */
const NEVER_ASKED = new Set(["end_user", "platform_admin"]);

/** Whether the status query should run at all. Mirrors the gate below. */
export function questionsGateEnabled(role: string | undefined, endOrgId: string | undefined): boolean {
  return !endOrgId && !!role && !NEVER_ASKED.has(role);
}

export interface QuestionsGateInput {
  role: string | undefined;
  /** Set when the session acts for a workspace it switched into. */
  endOrgId: string | undefined;
  /** Whether the status query has settled successfully. */
  isSuccess: boolean;
  status: OnboardingStatus | undefined;
}

/**
 * Should this person answer the signup questions before anything else?
 *
 * Only on a settled, successful read that says they have not finished: a slow
 * or failed onboarding API renders the dashboard, never a spinner or a trap.
 * `blocked` (competitor, disposable, unverifiable domain) is recorded server
 * side for follow-up and is not a reason to stop anyone.
 */
export function shouldAskQuestions(input: QuestionsGateInput): boolean {
  if (!questionsGateEnabled(input.role, input.endOrgId)) return false;
  if (!input.isSuccess || !input.status) return false;
  // `completed === false` exactly: the hook maps a 404 to `{}`, which says
  // nothing about this person and must not stop them.
  return input.status.completed === false && !input.status.blocked;
}
